class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 500);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
    
    // Initialize the observer to maintain our filter visibility changes
    this.setupFilterObserver();
    
    // Apply initial filter state if there are any active filters
    this.applyInitialFilterState();
    
    // Hide zero-count filter options
    this.hideZeroCountOptions();
    
    // Hide active filter tags/pills
    this.hideActiveFilterTags();
  }
  
  // Add this method to hide active filter tags/pills
  hideActiveFilterTags() {
    // Find and hide active filter sections
    const activeFilterSections = document.querySelectorAll('.active-facets-desktop, .active-facets-mobile');
    activeFilterSections.forEach(section => {
      section.style.display = 'none';
    });
  }
  
  // Add this new method to hide options with zero products
  hideZeroCountOptions() {
    // Find all filter option items
    const allOptionItems = document.querySelectorAll('.facets__item, .mobile-facets__item');
    
    allOptionItems.forEach(item => {
      // Look for the count text which is typically in a span next to the label
      const countText = item.textContent;
      
      // Check if the count is zero - looking for patterns like "(0)" or "0 products"
      if (countText.includes('(0)') || countText.includes('0)') || countText.match(/\b0\b/)) {
        // Completely hide the item
        item.style.display = 'none';
      }
    });
  }
  
  // Add this method to apply filter state on page load
  applyInitialFilterState() {
    // Check if there are any checked filters on page load
    const checkedFilters = document.querySelectorAll('input[type="checkbox"]:checked');
    
    checkedFilters.forEach(checkbox => {
      // For each checked filter, hide other options in the same group
      this.hideOtherOptionsInGroup(checkbox);
    });
  }
  
  // Helper method to hide other options in the same group
  hideOtherOptionsInGroup(checkbox) {
    // Find the parent details element (filter group)
    let filterGroup = checkbox.closest('details');
    if (!filterGroup) {
      // Try to find parent using js-filter class if details not found
      filterGroup = checkbox.closest('.js-filter');
    }
    
    if (!filterGroup) return;
    
    // Store filter info for re-application
    FacetFiltersForm.selectedFilters = FacetFiltersForm.selectedFilters || {};
    FacetFiltersForm.selectedFilters[filterGroup.dataset.index] = {
      value: checkbox.value,
      checked: true
    };
    
    // Find all option items in this group
    const optionItems = filterGroup.querySelectorAll('.facets__item, .mobile-facets__item');
    
    // Hide all options except the selected one
    optionItems.forEach(item => {
      const itemCheckbox = item.querySelector('input[type="checkbox"]');
      if (itemCheckbox && itemCheckbox !== checkbox) {
        // Use CSS to hide the item
        item.style.display = 'none';
      }
    });
  }
  
  // Add this new method to set up the MutationObserver
  setupFilterObserver() {
    // Create an object to store selected filters by group
    FacetFiltersForm.selectedFilters = {};
    
    // Create a MutationObserver to watch for changes to the filter elements
    const filterObserver = new MutationObserver((mutations) => {
      // Hide active filter tags/pills
      this.hideActiveFilterTags();
      
      // Hide zero-count options whenever the DOM changes
      this.hideZeroCountOptions();
      
      // If we have selected filters, re-apply our visibility changes
      if (Object.keys(FacetFiltersForm.selectedFilters).length > 0) {
        // For each filter group that has a selection
        Object.keys(FacetFiltersForm.selectedFilters).forEach(filterGroupIndex => {
          const filterInfo = FacetFiltersForm.selectedFilters[filterGroupIndex];
          
          if (!filterInfo.checked) return;
          
          // Find the filter group by data-index
          const filterGroups = document.querySelectorAll(`.js-filter[data-index="${filterGroupIndex}"], details[data-index="${filterGroupIndex}"]`);
          
          filterGroups.forEach(group => {
            // Find all option items in this group
            const optionItems = group.querySelectorAll('.facets__item, .mobile-facets__item');
            
            // Hide all options except the selected one
            optionItems.forEach(item => {
              const checkbox = item.querySelector('input[type="checkbox"]');
              if (checkbox && checkbox.value !== filterInfo.value) {
                item.style.display = 'none';
              }
            });
          });
        });
      }
    });
    
    // Start observing the entire facet filters form for changes
    filterObserver.observe(document.body, { 
      childList: true, 
      subtree: true 
    });
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll('.facets-container .loading__spinner, facet-filters-form .loading__spinner');
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
        
        // Hide active filter tags after rendering
        document.querySelector('facet-filters-form').hideActiveFilterTags();
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
    
    // Hide active filter tags after rendering
    document.querySelector('facet-filters-form').hideActiveFilterTags();
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;

    document
      .getElementById('ProductGridContainer')
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });
  }

  static renderProductCount(html) {
    const count = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount').innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll('.facets-container .loading__spinner, facet-filters-form .loading__spinner');
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');

    const facetDetailsElements = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const matchesIndex = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.dataset.index === jsFilter.dataset.index : false;
    };
    const facetsToRender = Array.from(facetDetailsElements).filter((element) => !matchesIndex(element));
    const countsToRender = Array.from(facetDetailsElements).find(matchesIndex);

    facetsToRender.forEach((element) => {
      document.querySelector(`.js-filter[data-index="${element.dataset.index}"]`).innerHTML = element.innerHTML;
    });

    // Skip rendering active facets to avoid the filter tags/pills
    // FacetFiltersForm.renderActiveFacets(parsedHTML);
    
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newElementSelector = document
          .getElementById(closestJSFilterID)
          .classList.contains('mobile-facets__details')
          ? `#${closestJSFilterID} .mobile-facets__close-button`
          : `#${closestJSFilterID} .facets__summary`;
        const newElementToActivate = document.querySelector(newElementSelector);
        if (newElementToActivate) newElementToActivate.focus();
      }
    }
    
    // Hide zero-count options after filters are rendered
    document.querySelector('facet-filters-form').hideZeroCountOptions();
    
    // Hide active filter tags after rendering
    document.querySelector('facet-filters-form').hideActiveFilterTags();
  }

  static renderActiveFacets(html) {
    // Skip this method to avoid rendering active facets
    return;
    
    /* Original code commented out
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
    */
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  // Method to handle filter option visibility within the same filter type
  handleFilterOptionVisibility(currentInput) {
    // Find the parent filter group
    let filterGroup = currentInput.closest('details');
    if (!filterGroup) {
      // Try to find parent using js-filter class if details not found
      filterGroup = currentInput.closest('.js-filter');
    }
    
    if (!filterGroup) return;
    
    if (currentInput.checked) {
      // Store information about the selected filter for re-application after rendering
      FacetFiltersForm.selectedFilters = FacetFiltersForm.selectedFilters || {};
      FacetFiltersForm.selectedFilters[filterGroup.dataset.index] = {
        value: currentInput.value,
        checked: true
      };
      
      // Hide other options in this group
      this.hideOtherOptionsInGroup(currentInput);
    } else {
      // When unchecked, update the stored filter info
      if (FacetFiltersForm.selectedFilters && filterGroup.dataset.index) {
        if (FacetFiltersForm.selectedFilters[filterGroup.dataset.index]) {
          FacetFiltersForm.selectedFilters[filterGroup.dataset.index].checked = false;
        }
      }
      
      // Show all options in this filter group
      const optionItems = filterGroup.querySelectorAll('.facets__item, .mobile-facets__item');
      optionItems.forEach(item => {
        item.style.display = '';
      });
      
      // Re-hide zero-count options
      this.hideZeroCountOptions();
    }
    
    // Always hide active filter tags
    this.hideActiveFilterTags();
  }

  // Method to clear other filters within the same filter type
  clearOtherFiltersInGroup(currentInput) {
    // Find the parent filter group
    let filterGroup = currentInput.closest('details');
    if (!filterGroup) {
      // Try to find parent using js-filter class if details not found
      filterGroup = currentInput.closest('.js-filter');
    }
    
    if (!filterGroup) return;
    
    // Find all checkbox inputs within this filter group
    const checkboxes = filterGroup.querySelectorAll('input[type="checkbox"]');
    
    // Uncheck all other checkboxes in this group
    checkboxes.forEach(checkbox => {
      if (checkbox !== currentInput && checkbox.checked) {
        checkbox.checked = false;
      }
    });
    
    // Handle filter option visibility
    this.handleFilterOptionVisibility(currentInput);
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    
    // Handle checkbox changes
    if (event.target.type === 'checkbox') {
      this.clearOtherFiltersInGroup(event.target);
    }
    
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            const noJsElements = document.querySelectorAll('.no-js-list');
            noJsElements.forEach((el) => el.remove());
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    
    // Get the filter value and index from the active filter
    const filterValue = event.currentTarget.dataset.value;
    const filterIndex = event.currentTarget.dataset.index;
    
    // Clear the selected filter info when removing a filter
    if (filterIndex && FacetFiltersForm.selectedFilters && FacetFiltersForm.selectedFilters[filterIndex]) {
      FacetFiltersForm.selectedFilters[filterIndex].checked = false;
    }
    
    FacetFiltersForm.renderPage(url);
  }
}

// Add this static property to store the selected filter information
FacetFiltersForm.selectedFilters = {};
FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) =>
      element.addEventListener('change', this.onRangeChange.bind(this))
    );
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('min', 0);
    if (maxInput.value === '') minInput.setAttribute('max', maxInput.getAttribute('max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('min'));
    const max = Number(input.getAttribute('max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);